import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService } from '../../shared/services/api/authentication/authentication.service';
import { UserSessionStore } from '../../shared/services/user-session.store';
import { ExplainModuleEnum } from '../../core/user/user.model';

type PageState = 'loading' | 'success' | 'error';

const REDIRECT_FALLBACK_DELAY_MS = 500;

@Component({
  templateUrl: './addin-connect.component.html',
  styleUrls: ['./addin-connect.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddinConnectComponent implements OnInit {
  state: PageState = 'loading';
  errorTitle = '';
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private authService: AuthenticationService,
    private userSessionStore: UserSessionStore
  ) {}

  async ngOnInit(): Promise<void> {
    const uid = this.route.snapshot.queryParamMap.get('uid');
    if (!uid) {
      this.setError('addin-connect.error-title', 'addin-connect.error-missing-uid');
      return;
    }

    const cookieSession = await this.authService.authenticateWithCookie().catch(() => null);
    const userSession = cookieSession ?? this.userSessionStore.userSession();

    const userModules = userSession?.modules ?? [];
    if (userSession && !userModules.some(m => m.name === ExplainModuleEnum.RESPONSE)) {
      this.setError('addin-connect.error-title-missing-module', 'addin-connect.error-missing-response-module');
      return;
    }

    try {
      await firstValueFrom(this.authService.verifyAddinCreation(uid));
      this.state = 'success';
    } catch (err) {
      const error = err as { status?: number };
      switch (error.status) {
        case 401:
          this.userSessionStore.clearSession();
          this.setError('', 'addin-connect.error-unauthorized');
          break;
        case 400:
          this.setError('addin-connect.error-title', 'addin-connect.error-bad-request');
          break;
        case 403:
          this.setError('addin-connect.error-title', 'addin-connect.error-forbidden');
          break;
        case 409:
          this.setError('addin-connect.error-title', 'addin-connect.error-already-exists');
          break;
        case 429:
          this.setError('addin-connect.error-title', 'addin-connect.error-too-many-requests');
          break;
        default:
          this.setError('addin-connect.error-title', 'addin-connect.error-generic');
      }
    }
  }

  private setError(title: string, message: string): void {
    this.state = 'error';
    this.errorTitle = title;
    this.errorMessage = message;
  }

  redirectToLogin(): void {
    this.router.navigate(['/login'], { queryParams: { fromUrl: this.location.path(true) } });
  }

  redirectToProjects(): void {
    this.router.navigate(['/tenders/responses']);
  }

  closeOrRedirect(): void {
    window.close();
    setTimeout(() => this.router.navigate(['/login']), REDIRECT_FALLBACK_DELAY_MS);
  }
}
